import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useClasses } from '@/data/classes';
import { useCompanies } from '@/data/companies';
import { useLocations } from '@/data/programs';
import { useScheduleConflicts } from '@/data/sessions';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { WeeklyGrid } from '@/components/schedule/WeeklyGrid';
import { formatTime12 } from '@/lib/time';
import type { Class, DeliveryMode, ProgramType } from '@pirouette/shared';

export const Route = createFileRoute('/admin/classes/')({
  component: ClassesIndex,
});

interface Term {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
}

type ClassWithProgramDetails = Class & {
  program_offerings?: { id: string; name: string; program_type: ProgramType } | null;
  studio_locations?: { id: string; name: string; kind: string } | null;
};

const programTypeOptions: { value: ProgramType; label: string }[] = [
  { value: 'recurring_class', label: 'Recurring class' },
  { value: 'camp', label: 'Camp' },
  { value: 'intensive', label: 'Intensive' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'private_lesson', label: 'Private lesson' },
  { value: 'semi_private', label: 'Semi-private' },
  { value: 'trio', label: 'Trio' },
  { value: 'drop_in', label: 'Drop-in' },
  { value: 'adult_membership', label: 'Adult membership' },
  { value: 'mobile_preschool', label: 'Mobile preschool' },
  { value: 'rental', label: 'Rental' },
  { value: 'event_choreography', label: 'Event choreography' },
  { value: 'performance_group', label: 'Performance group' },
  { value: 'competition_team', label: 'Competition team' },
];

const deliveryModeLabels: Record<DeliveryMode, string> = {
  in_person: 'In person',
  online: 'Online',
  hybrid: 'Hybrid',
  offsite: 'Offsite',
};

function useCurrentTerm() {
  return useQuery({
    queryKey: ['terms', 'current'],
    queryFn: async (): Promise<Term | null> => {
      const { data, error } = await supabase
        .from('terms')
        .select('id, name, start_date, end_date, is_current')
        .eq('is_current', true)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

function ClassesIndex() {
  const navigate = useNavigate();
  const term = useCurrentTerm();
  const companies = useCompanies();
  const locations = useLocations();
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [programType, setProgramType] = useState<ProgramType | ''>('');
  const [companyId, setCompanyId] = useState('');
  const [locationId, setLocationId] = useState('');

  const classes = useClasses({
    termId: term.data?.id,
    programType: programType || undefined,
    companyId: companyId || undefined,
    locationId: locationId || undefined,
  });
  const conflicts = useScheduleConflicts(term.data?.id);

  const visibleClassIds = useMemo(
    () => new Set((classes.data ?? []).map((c) => c.id)),
    [classes.data],
  );

  const filteredConflicts = useMemo(() => {
    const allConflicts = conflicts.data ?? [];
    const hasListFilter = Boolean(companyId || programType || locationId);
    if (!hasListFilter) return allConflicts;
    if (!classes.data) return [];
    return allConflicts.filter(
      (c) => visibleClassIds.has(c.class_a_id) || visibleClassIds.has(c.class_b_id),
    );
  }, [classes.data, companyId, conflicts.data, locationId, programType, visibleClassIds]);

  const conflictedIds = useMemo(() => {
    const s = new Set<string>();
    for (const c of filteredConflicts) {
      s.add(c.class_a_id);
      s.add(c.class_b_id);
    }
    return s;
  }, [filteredConflicts]);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Schedule
          </p>
          <h1 className="mt-2">Classes</h1>
          {term.data && (
            <p className="mt-1 text-[var(--color-ink-muted)]">
              {term.data.name} · {term.data.start_date} → {term.data.end_date}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <div
            role="group"
            aria-label="View mode"
            className="inline-flex rounded-[var(--radius-md)] border border-[var(--color-border)] p-0.5"
          >
            <button
              type="button"
              onClick={() => setView('grid')}
              aria-pressed={view === 'grid'}
              className="rounded-[var(--radius-sm)] px-3 py-1 text-[var(--text-sm)] aria-[pressed=true]:bg-[var(--color-surface-sunken)] aria-[pressed=true]:font-medium"
            >
              Weekly grid
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              aria-pressed={view === 'list'}
              className="rounded-[var(--radius-sm)] px-3 py-1 text-[var(--text-sm)] aria-[pressed=true]:bg-[var(--color-surface-sunken)] aria-[pressed=true]:font-medium"
            >
              List
            </button>
          </div>
          <Link to="/admin/classes/new">
            <Button>New class</Button>
          </Link>
        </div>
      </div>

      <section aria-label="Class filters" className="surface flex flex-wrap items-end gap-4 p-4">
        <div>
          <label
            htmlFor="class-program-type-filter"
            className="block text-[var(--color-ink-muted)] text-[var(--text-sm)]"
          >
            Program type
          </label>
          <select
            id="class-program-type-filter"
            value={programType}
            onChange={(event) => setProgramType(event.target.value as ProgramType | '')}
            className="mt-1 h-10 min-w-48 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
          >
            <option value="">All program types</option>
            {programTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="class-company-filter"
            className="block text-[var(--color-ink-muted)] text-[var(--text-sm)]"
          >
            Company
          </label>
          <select
            id="class-company-filter"
            value={companyId}
            onChange={(event) => setCompanyId(event.target.value)}
            disabled={companies.isLoading || companies.isError}
            className="mt-1 h-10 min-w-48 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
          >
            <option value="">
              {companies.isLoading
                ? 'Loading companies...'
                : companies.isError
                  ? 'Companies unavailable'
                  : 'All companies'}
            </option>
            {companies.data?.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name} · {company.season}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="class-location-filter"
            className="block text-[var(--color-ink-muted)] text-[var(--text-sm)]"
          >
            Location
          </label>
          <select
            id="class-location-filter"
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
            className="mt-1 h-10 min-w-48 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
          >
            <option value="">All locations</option>
            {locations.data?.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      {companies.isError && (
        <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
          Company filters could not be loaded.
        </p>
      )}

      {filteredConflicts.length > 0 && (
        <aside
          role="alert"
          aria-labelledby="conflicts-heading"
          className="surface space-y-3 border border-[var(--color-danger)] bg-[var(--color-danger-muted)] p-5"
        >
          <h2 id="conflicts-heading" className="font-medium text-[var(--text-base)]">
            {filteredConflicts.length} scheduling{' '}
            {filteredConflicts.length === 1 ? 'conflict' : 'conflicts'}
          </h2>
          <ul className="space-y-1.5 text-[var(--text-sm)]">
            {filteredConflicts.map((c) => (
              <li
                // Include shared_value: two classes can share BOTH a room
                // and an instructor, or share multiple instructors —
                // (class_a_id, class_b_id, kind) is not unique by itself.
                key={`${c.class_a_id}-${c.class_b_id}-${c.kind}-${c.shared_value}`}
                className="flex flex-wrap gap-2"
              >
                <Badge tone="danger">{c.kind}</Badge>
                <span>
                  <strong>{c.class_a_name}</strong> and <strong>{c.class_b_name}</strong> share{' '}
                  <em className="not-italic">{c.shared_value}</em> on {c.weekday}.
                </span>
              </li>
            ))}
          </ul>
        </aside>
      )}

      {classes.isLoading && <p aria-live="polite">Loading…</p>}

      {classes.data?.length === 0 && (
        <Empty
          title="No classes yet"
          description="Add the first class to start building the studio schedule."
          action={
            <Link to="/admin/classes/new">
              <Button>Add a class</Button>
            </Link>
          }
        />
      )}

      {classes.data && classes.data.length > 0 && view === 'grid' && (
        <WeeklyGrid
          classes={classes.data}
          conflictedClassIds={conflictedIds}
          onSelect={(id) => void navigate({ to: '/admin/classes/$id', params: { id } })}
        />
      )}

      {classes.data && classes.data.length > 0 && view === 'list' && (
        <div role="region" aria-label="Classes table" className="surface overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-[var(--color-surface-sunken)] text-[var(--text-sm)]">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">
                  Class
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Day · time
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Room
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Tuition
                </th>
                <th scope="col" className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {classes.data.map((c) => {
                const classDetails = c as ClassWithProgramDetails;
                return (
                  <tr
                    key={c.id}
                    className="border-t border-[var(--color-border)] hover:bg-[var(--color-surface-sunken)]"
                  >
                    <td className="px-4 py-3">
                      <span className="font-medium">{c.name}</span>
                      <span className="ml-2 text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                        {c.style} · {c.level}
                      </span>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {classDetails.program_offerings && (
                          <Badge>
                            {classDetails.program_offerings.program_type.replaceAll('_', ' ')}
                          </Badge>
                        )}
                        {classDetails.studio_locations && (
                          <Badge tone="accent">{classDetails.studio_locations.name}</Badge>
                        )}
                        <Badge tone="neutral">{deliveryModeLabels[c.delivery_mode]}</Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[var(--color-ink-muted)] capitalize">
                      {c.weekday} · {formatTime12(c.start_time)}–{formatTime12(c.end_time)}
                    </td>
                    <td className="px-4 py-3 text-[var(--color-ink-muted)]">{c.room}</td>
                    <td className="px-4 py-3 text-[var(--color-ink-muted)] tabular-nums">
                      ${(c.tuition_cents / 100).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        {conflictedIds.has(c.id) && <Badge tone="danger">conflict</Badge>}
                        <Link
                          to="/admin/classes/$id"
                          params={{ id: c.id }}
                          className="text-[var(--color-accent)] underline-offset-2 hover:underline"
                        >
                          Open
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
